import {
  COMMON_APPLICATION_ACTION,
  COMMON_REGISTRATION_ACTION,
  CommonApplicationAction,
  trackCommonApplicationEvent,
} from '@src/features/common-application/analytics';
import {
  ENTRY_TRACKED_KEY,
  LAST_STEP_KEY,
  ONBOARDING_ENTRY_KEY,
  VISITED_STEPS_KEY,
} from '@src/features/common-application/constants';
import { CommonApplicationResponse } from '@src/features/common-application/types/common-application';
import { useEffect, useState } from 'react';

type EntryContext = {
  /** This browser has opened the application before. */
  visitedBefore: boolean;
  /** Rails handed off with ?entry=onboarding. */
  fromOnboarding: boolean;
  startedByAdvisor: boolean;
  createdAt: string;
  updatedAt: string;
};

/**
 * Whether entering the wizard starts the application or continues it.
 *
 * The server creates a draft in one save, so a draft whose updatedAt is past
 * its createdAt has been saved since. Drafts are pre-filled from the profile,
 * so their fields can't tell. startedByAdvisor drafts may have been saved by
 * the advisor, so only this browser's record of a visit counts for them: a
 * member's first visit to one starts the application.
 *
 * Rails sends ?entry=onboarding after registration, but also after checking
 * setup and from old /common_application links, so Common Registration V2
 * slightly overcounts registrations.
 *
 * Rails sets startedByAdvisor for every advisor-invited member until they
 * first submit (a common registration and no bank applications). Since only
 * this browser's record counts for them, the same member sends Start again
 * on another device or browser, or after clearing site data. Accepted: it
 * slightly inflates Starts for that cohort, not the step funnel or Submits.
 */
export function getEntryEvent(context: EntryContext): {
  name: 'Start' | 'Continue';
  action: CommonApplicationAction;
} {
  const savedSinceCreated =
    Date.parse(context.updatedAt) > Date.parse(context.createdAt);
  const continuing =
    context.visitedBefore || (!context.startedByAdvisor && savedSinceCreated);
  if (continuing) {
    return { name: 'Continue', action: COMMON_APPLICATION_ACTION };
  }
  return {
    name: 'Start',
    action: context.fromOnboarding
      ? COMMON_REGISTRATION_ACTION
      : COMMON_APPLICATION_ACTION,
  };
}

function readStorage(storage: () => Storage, key: string): string | null {
  try {
    return storage().getItem(key);
  } catch {
    return null;
  }
}

/** The wizard records the step and the steps seen on every visit. */
function hasRecordedVisit(uuid: string): boolean {
  return [LAST_STEP_KEY, VISITED_STEPS_KEY].some(
    (prefix) => readStorage(() => localStorage, `${prefix}${uuid}`) !== null,
  );
}

function writeSession(key: string, value: string | null) {
  try {
    if (value === null) {
      sessionStorage.removeItem(key);
    } else {
      sessionStorage.setItem(key, value);
    }
  } catch {
    // Storage blocked (private browsing): a refresh may send the event again.
  }
}

/**
 * End the wizard visit, so the next entry sends Start or Continue again. The
 * route calls this when the user navigates out of the wizard, and when Rails
 * hands off to it, since a full page load to Rails never runs the route's
 * leave hook.
 */
export function endTrackedEntry() {
  writeSession(ENTRY_TRACKED_KEY, null);
}

/**
 * Send Start or Continue once per visit to the wizard. The visit lasts until
 * endTrackedEntry, so a refresh, a remount after a step error, or StrictMode's
 * second effect run sends nothing more.
 */
export function useTrackApplicationEntry(
  application: CommonApplicationResponse,
) {
  // Read on the first render, before the layout's effects record this visit
  // (the last step and the visited steps), which would make it look like a
  // return.
  const [entry] = useState(() => ({
    uuid: application.uuid,
    context: {
      visitedBefore: hasRecordedVisit(application.uuid),
      fromOnboarding:
        readStorage(() => sessionStorage, ONBOARDING_ENTRY_KEY) === 'true',
      startedByAdvisor: application.startedByAdvisor,
      createdAt: application.createdAt,
      updatedAt: application.updatedAt,
    },
  }));
  useEffect(() => {
    if (readStorage(() => sessionStorage, ENTRY_TRACKED_KEY) !== entry.uuid) {
      const { name, action } = getEntryEvent(entry.context);
      trackCommonApplicationEvent({ name, action });
      writeSession(ENTRY_TRACKED_KEY, entry.uuid);
    }
    writeSession(ONBOARDING_ENTRY_KEY, null);
  }, [entry]);
}
